import { useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import {
	useDeletePaymentMutation,
	useListPaymentsQuery,
	type Payment,
} from "../../store/api/paymentsApi";
import { arrayToCsv, downloadCsv } from "../../utils/csvExport";
import BulkActions from "./components/BulkActions";
import PaymentDetailModal from "./components/PaymentDetailModal";
import PaymentFilters from "./components/PaymentFilters";
import PaymentStats from "./components/PaymentStats";
import PaymentTable from "./components/PaymentTable";
import RecordPaymentModal from "./components/RecordPaymentModal";

const PaymentManagement = () => {
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState({
		status: "",
	});
	const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
	const [selectedPayment, setSelectedPayment] = useState<any>(null);
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
	const [isRecordPaymentOpen, setIsRecordPaymentOpen] = useState(false);

	const {
		data: paymentsData,
		isLoading,
		error,
		refetch,
	} = useListPaymentsQuery({
		page,
		page_size: 20,
		status: filters.status || undefined,
		ordering: "-due_date",
	});

	const [deletePayment, { isLoading: isDeleting }] = useDeletePaymentMutation();

	const handleFilterChange = (newFilters: { status?: string }) => {
		setFilters((prev) => ({ ...prev, ...newFilters }));
		setPage(1);
	};

	const handlePaymentSelect = (paymentId: string, selected: boolean) => {
		if (selected) {
			setSelectedPayments((prev) => [...prev, paymentId]);
		} else {
			setSelectedPayments((prev) => prev.filter((id) => id !== paymentId));
		}
	};

	const handleSelectAll = (selected: boolean) => {
		if (!paymentsData) return;
		if (selected) {
			setSelectedPayments(paymentsData.results.map((p) => p.id));
		} else {
			setSelectedPayments([]);
		}
	};

	const handlePaymentClick = (payment: any) => {
		setSelectedPayment(payment);
		setIsDetailModalOpen(true);
	};

	const handleDeletePayment = async (paymentId: string) => {
		if (!window.confirm("Are you sure you want to delete this payment?")) {
			return;
		}

		try {
			await deletePayment(paymentId).unwrap();
			refetch();
			setSelectedPayments((prev) => prev.filter((id) => id !== paymentId));
		} catch (error) {
			console.error("Failed to delete payment:", error);
			alert("Failed to delete payment. Please try again.");
		}
	};

	const handleRecordPayment = () => {
		setIsRecordPaymentOpen(true);
	};

	const handleRecordPaymentSuccess = () => {
		setIsRecordPaymentOpen(false);
		setSelectedPayments([]);
		refetch();
	};

	const handleBulkAction = async (action: string) => {
		switch (action) {
			case "export":
				// Export functionality
				if (!paymentsData) return;
				const csvData = paymentsData.results.map((payment) => ({
					Tenant: payment.tenant_name || "N/A",
					Property: payment.property_name || "N/A",
					Amount: payment.amount?.toString() || "0",
					"Due Date": payment.due_date || "",
					Status: payment.status || "",
					Method: payment.payment_method || "N/A",
				}));

				const csvContent = arrayToCsv(csvData, [
					"Tenant",
					"Property",
					"Amount",
					"Due Date",
					"Status",
					"Method",
				]);
				downloadCsv(csvContent, "payments-export");
				break;
			case "delete":
				if (
					!window.confirm(
						`Are you sure you want to delete ${selectedPayments.length} payments?`,
					)
				) {
					return;
				}
				try {
					await Promise.all(
						selectedPayments.map((id) => deletePayment(id).unwrap()),
					);
					refetch();
					setSelectedPayments([]);
				} catch (error) {
					console.error("Failed to delete payments:", error);
					alert("Failed to delete some payments. Please try again.");
				}
				break;
			default:
				break;
		}
	};

	if (error) {
		return (
			<div className="w-full flex flex-col items-center justify-center min-h-screen space-y-4">
				<Icon name="AlertCircle" size={48} className="text-destructive" />
				<h2 className="text-xl font-semibold text-foreground">
					Error Loading Payments
				</h2>
				<p className="text-muted-foreground">
					Failed to load payments. Please try again.
				</p>
				<Button onClick={() => refetch()}>Retry</Button>
			</div>
		);
	}

	const payments = paymentsData?.results || [];

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="mb-4 sm:mb-6">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div>
							<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
								Lease Payments
							</h1>
							<p className="text-muted-foreground mt-1">
								Manage ongoing rental payments, record transactions, and track
								lease payment history.
							</p>
						</div>

						<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:flex-shrink-0">
							<Button
								variant="outline"
								onClick={() => handleBulkAction("export")}
								iconName="Download"
								iconPosition="left"
								className="w-full sm:w-auto"
							>
								Export
							</Button>

							<Button
								onClick={handleRecordPayment}
								iconName="Plus"
								iconPosition="left"
								className="w-full sm:w-auto"
							>
								Record Payment
							</Button>
						</div>
					</div>
				</div>

				{/* Main Content Grid */}
				<div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6">
					{/* Filters & Stats - Full width on mobile, 3 cols on desktop */}
					<div className="xl:col-span-3 space-y-6">
						<PaymentFilters
							filters={filters}
							onFilterChange={handleFilterChange}
							paymentCount={payments.length}
						/>

						<PaymentStats payments={payments} />
					</div>

					{/* Main Table Area - 9 cols on desktop */}
					<div className="xl:col-span-9 space-y-6">
						{selectedPayments.length > 0 && (
							<BulkActions
								selectedCount={selectedPayments.length}
								onBulkAction={handleBulkAction}
								onClearSelection={() => setSelectedPayments([])}
							/>
						)}

						{isLoading ? (
							<Loader
								size="lg"
								showText
								text="Loading payments..."
								className="h-64"
							/>
						) : (
							<>
								<PaymentTable
									payments={payments}
									selectedPayments={selectedPayments}
									onPaymentSelect={handlePaymentSelect}
									onSelectAll={handleSelectAll}
									onPaymentClick={handlePaymentClick}
									onRecordPayment={(paymentId) => {
										const payment = payments.find((p) => p.id === paymentId);
										if (payment) {
											setSelectedPayment(payment);
											setIsRecordPaymentOpen(true);
										}
									}}
									onDeletePayment={handleDeletePayment}
								/>

								{/* Pagination */}
								{paymentsData &&
									(paymentsData.next || paymentsData.previous) && (
										<div className="flex items-center justify-between">
											<Button
												variant="outline"
												onClick={() => setPage((p) => Math.max(1, p - 1))}
												disabled={!paymentsData.previous}
												iconName="ChevronLeft"
												iconPosition="left"
											>
												Previous
											</Button>
											<span className="text-sm text-muted-foreground">
												Page {page} of {Math.ceil(paymentsData.count / 20)}
											</span>
											<Button
												variant="outline"
												onClick={() => setPage((p) => p + 1)}
												disabled={!paymentsData.next}
												iconName="ChevronRight"
												iconPosition="right"
											>
												Next
											</Button>
										</div>
									)}

								{/* Empty State */}
								{payments.length === 0 && (
									<div className="bg-card rounded-lg border border-border p-6 sm:p-12 text-center">
										<Icon
											name="CreditCard"
											size={64}
											className="text-muted-foreground mx-auto mb-4"
										/>
										<h3 className="text-lg font-medium text-foreground mb-2">
											No payments found
										</h3>
										<p className="text-muted-foreground mb-6">
											No payments match your current filters. Try adjusting your
											search criteria.
										</p>
										<Button
											onClick={handleRecordPayment}
											iconName="Plus"
											iconPosition="left"
										>
											Record First Payment
										</Button>
									</div>
								)}
							</>
						)}
					</div>
				</div>

				{/* Payment Detail Modal */}
				<PaymentDetailModal
					payment={selectedPayment}
					isOpen={isDetailModalOpen}
					onClose={() => {
						setIsDetailModalOpen(false);
						setSelectedPayment(null);
					}}
					onUpdate={refetch}
				/>

				{/* Record Payment Modal */}
				<RecordPaymentModal
					isOpen={isRecordPaymentOpen}
					onClose={() => {
						setIsRecordPaymentOpen(false);
						setSelectedPayment(null);
						setSelectedPayments([]);
					}}
					selectedPayments={selectedPayments
						.map((id) => payments.find((p) => p.id === id))
						.filter((p): p is Payment => p !== undefined)}
					onSuccess={handleRecordPaymentSuccess}
				/>
			</div>
		</div>
	);
};

export default PaymentManagement;
